// 🎛️ LOGO DOCK — logo-first strip view: bottom random logos, tap → hero card + matching category row on top.
import { useEffect, useState } from "react";
import axios from "axios";
import { logoFor } from "@/lib/brandLogos";
import { getIcon } from "./iconMap";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Tile = ({ b, onPick, small, wide }) => {
  const logo = logoFor(b, null, BACKEND_URL);
  const Ico = getIcon(b.icon);
  if (wide) {
    return (
      <button data-testid="dock-super-tile" onClick={() => onPick(b)} title="SUPER POG subscriber — premium wide tile"
        className="relative shrink-0 h-14 w-36 rounded-2xl overflow-hidden border-2 border-amber-400/80 shadow-[0_0_14px_rgba(251,191,36,0.55)] bg-white flex items-center gap-2 px-2 active:scale-95 transition-transform">
        {logo ? <img src={logo} alt={b.name} loading="lazy" className="w-10 h-10 object-contain shrink-0" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          : <span className="font-heading font-black text-xl text-slate-700 shrink-0 w-10 text-center">{(b.name || "?")[0].toUpperCase()}</span>}
        <span className="min-w-0 text-left">
          <span className="block text-[11px] font-heading font-black text-slate-900 truncate">{b.name}</span>
          <span className="block text-[8px] font-black text-amber-600 tracking-widest">⭐ SUPER POG</span>
        </span>
        {(b.has_deals || b.is_sale) && <span className="absolute bottom-0.5 right-1 text-[12px]">🔥</span>}
      </button>
    );
  }
  const sz = small ? "w-14 h-14" : "w-16 h-16";
  return (
    <button data-testid="dock-logo-tile" onClick={() => onPick(b)}
      className={`relative shrink-0 ${sz} rounded-2xl overflow-hidden border border-white/25 shadow-lg bg-slate-800 active:scale-95 transition-transform`}>
      {logo ? (
        <img src={logo} alt={b.name} loading="lazy" className="absolute inset-0 w-full h-full object-contain bg-white scale-[1.12]"
          onError={(e) => { e.currentTarget.style.display = "none"; }} />
      ) : (
        <span className="absolute inset-0 flex flex-col items-center justify-center text-white">
          <span className="font-heading font-black text-base leading-none">{(b.name || "?")[0].toUpperCase()}</span>
          <Ico className="w-3.5 h-3.5 opacity-70 mt-0.5" />
        </span>
      )}
      {(b.has_deals || b.is_sale) && <span className="absolute bottom-0 right-0.5 text-[13px] drop-shadow">🔥</span>}
    </button>
  );
};

export const LogoDock = ({ businesses, onTabClick, onFav, isFav, onToggleView }) => {
  const [picked, setPicked] = useState(null);
  // 🚀 SUPER POG SPOTLIGHT — subscribers take turns in a glowing hero pill above the dock
  const supers = businesses.filter((b) => b.super_pog);
  const [spotIdx, setSpotIdx] = useState(0);
  useEffect(() => {
    if (supers.length < 2) return;
    const t = setInterval(() => setSpotIdx((i) => i + 1), 12000);
    return () => clearInterval(t);
  }, [supers.length]);
  const spot = supers.length ? supers[spotIdx % supers.length] : null;
  // 📊 POG TIMES — busy-hours bars for the picked merchant
  const [pogTimes, setPogTimes] = useState(null);
  useEffect(() => {
    setPogTimes(null);
    if (!picked) return;
    axios.get(`${BACKEND_URL}/api/pog-times/${encodeURIComponent(picked.id)}`)
      .then((r) => setPogTimes(r.data)).catch(() => {});
  }, [picked]);
  const matches = picked
    ? businesses.filter((x) => x.category_id === picked.category_id && x.id !== picked.id).slice(0, 14)
    : [];
  return (
    <>
      {/* 🦸 HERO CARD — the big pulled-up glass card */}
      {picked && (
        <div data-testid="dock-hero-card" className="absolute inset-0 z-[1200] flex items-center justify-center p-6" onClick={() => setPicked(null)}>
          <div onClick={(e) => e.stopPropagation()}
            className="w-full max-w-[300px] rounded-3xl border border-white/30 shadow-2xl p-5 text-center"
            style={{ background: "rgba(24,24,26,0.55)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)" }}>
            <div className="mx-auto w-40 h-40 rounded-3xl overflow-hidden bg-white border border-white/40 relative">
              {logoFor(picked, null, BACKEND_URL) ? (
                <img src={logoFor(picked, null, BACKEND_URL)} alt={picked.name} className="absolute inset-0 w-full h-full object-contain scale-[1.1]" />
              ) : (
                <span className="absolute inset-0 flex items-center justify-center font-heading font-black text-5xl text-slate-700">{(picked.name || "?")[0].toUpperCase()}</span>
              )}
            </div>
            <p className="mt-3 font-heading font-black text-white text-lg leading-tight">{picked.name}</p>
            <p className="text-white/90 text-sm font-bold">{picked.distance_miles} mi{picked.has_deals ? " · 🔥 live deal" : ""}</p>
            {pogTimes && (
              <div className="mt-3" data-testid="dock-pog-times">
                <p className="text-[9px] font-heading font-black tracking-[0.25em] text-cyan-300">
                  📊 POG TIMES{pogTimes.estimated ? <span className="text-white/50"> · EST.</span> : ""}
                </p>
                <div className="mt-1.5 flex items-end justify-center gap-[3px] h-9">
                  {pogTimes.bars.map((v, i) => (
                    <span key={i} title={`${((pogTimes.start_hour + i - 1) % 12) + 1}${pogTimes.start_hour + i < 12 ? "am" : "pm"}`}
                      className={`w-[7px] rounded-t ${v > 0.75 ? "bg-cyan-300" : "bg-cyan-400/45"}`} style={{ height: `${Math.max(12, v * 100)}%` }} />
                  ))}
                </div>
                <div className="flex justify-between text-[8px] font-bold text-white/50 px-3 mt-0.5"><span>9a</span><span>2p</span><span>8p</span></div>
              </div>
            )}
            <div className="mt-4 flex items-center justify-center gap-3">
              <button data-testid="dock-hero-open" onClick={() => { const b = picked; setPicked(null); onTabClick && onTabClick(b); }}
                className="h-11 px-5 rounded-full bg-gradient-to-r from-fuchsia-500 to-violet-600 text-white font-heading font-black text-sm active:scale-95 transition-transform">
                🎯 OPEN POG CARD
              </button>
              <button data-testid="dock-hero-fav" onClick={() => onFav && onFav(picked)}
                className="h-11 w-11 rounded-full bg-white/15 border border-white/30 text-lg active:scale-95 transition-transform">
                {isFav && isFav(picked.id) ? "❤️" : "🤍"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="absolute bottom-0 left-0 w-full z-[1000] px-3 pb-[calc(4.5rem+env(safe-area-inset-bottom))] pt-2 flex flex-col gap-2 pointer-events-none">
        <div className="pointer-events-auto self-start ml-1 flex items-center gap-2">
          <span className="rounded-full px-3 py-1.5 text-[11px] font-heading font-black text-fuchsia-200 bg-slate-900/80 border border-white/15 shadow-lg">🎛️ THE POG STRIP · LOGO VIEW</span>
          <button data-testid="strip-view-toggle" onClick={onToggleView}
            className="rounded-full px-3 py-1.5 text-[11px] font-heading font-black text-white bg-gradient-to-r from-fuchsia-600 to-violet-600 shadow-lg active:scale-95 transition-transform">
            🃏 CARD VIEW
          </button>
        </div>
        {/* 🚀 SUPER POG SPOTLIGHT — the perk that sells itself */}
        {spot && !picked && (
          <button data-testid="dock-super-spotlight" onClick={() => setPicked(spot)}
            className="pointer-events-auto self-start ml-1 flex items-center gap-2 rounded-full pl-2 pr-3 py-1.5 bg-slate-900/85 border-2 border-amber-400/70 shadow-[0_0_14px_rgba(251,191,36,0.45)] active:scale-95 transition-transform">
            <span className="text-[13px] leading-none">🚀</span>
            <span className="text-[11px] font-heading font-black text-amber-300 truncate max-w-[180px]">SUPER POG SPOTLIGHT · {spot.name}</span>
            <span className="text-[10px] font-bold text-white/70">{spot.distance_miles > 0 ? `${spot.distance_miles} mi` : "HERE"}</span>
          </button>
        )}
        {/* 🔝 SPECIFIC ON TOP — same-category brands appear after a pick */}
        {picked && matches.length > 0 && (
          <div data-testid="dock-top-row" className="pointer-events-auto flex gap-2 overflow-x-auto no-scrollbar py-1">
            <span className="shrink-0 self-center text-[10px] font-black uppercase tracking-widest text-amber-300 bg-slate-900/80 rounded-full px-2 py-1 border border-amber-400/30">{(picked.category_label || "MORE").toUpperCase()} NEARBY →</span>
            {matches.map((b) => <Tile key={`t-${b.id}`} b={b} onPick={setPicked} small wide={!!b.super_pog} />)}
          </div>
        )}
        {/* 🎲 RANDOM / NEARBY LOGOS — the bottom dock */}
        <div data-testid="dock-bottom-row" className="pointer-events-auto flex gap-2 overflow-x-auto no-scrollbar py-1">
          {businesses.slice(0, 30).map((b) => <Tile key={`b-${b.id}`} b={b} onPick={setPicked} wide={!!b.super_pog} />)}
        </div>
      </div>
    </>
  );
};
